import React from "react";
import { Link } from "react-router-dom";
import { Pill } from "../../common/DataList";
import colors from "../../../helpers/colors";
import tools from "../../../helpers/tools";
import { STATUT_COLORS, STATUT_TONES, demandeur, nomComplet } from "../SuspensionRemobilisation/suspensionRemobilisationLabels";
import VoirCourrierRemobilisation from "./VoirCourrierRemobilisation";

const epLiee = (d) => (d.targetedSanction?.epDate ? ` — sanction de l'EP du ${tools.formatDate(d.targetedSanction.epDate)}` : "");

const TITRES = {
  demande: (d) => (d.status === "LEVEE_DE_SAISINE" ? "Levée de saisine enregistrée" : `${d.outcomeLabel} demandée${epLiee(d)}`),
  retrait: (d) =>
    d.epRegistrationWithdrawal?.done
      ? `Inscription à l'EP du ${tools.formatDate(d.epRegistrationWithdrawal.epDate)} retirée de l'ordre du jour`
      : `Inscription à l'EP du ${tools.formatDate(d.epRegistrationWithdrawal?.epDate)} non retirée`,
  decision: (d) => `${d.outcomeLabel} ${d.status === "VALIDEE" ? "validée" : "refusée"} par le Directeur${epLiee(d)}`,
};

const ICONES = { demande: "unlock-alt", retrait: "calendar-times", decision: "gavel" };

const couleur = (event) => {
  const { etape, demande } = event;
  if (etape === "retrait") return demande.epRegistrationWithdrawal?.done ? "success" : "warning";
  if (etape === "decision") return demande.status === "VALIDEE" ? "success" : "danger";
  return STATUT_COLORS[demande.status] ?? "dark";
};

// L'EP dont la sanction visée est issue, cliquable quand on la connaît.
const lienEP = (d) => {
  const s = d.targetedSanction;
  if (!s?.epDate) return null;
  const date = tools.formatDate(s.epDate);
  return s.epId ? (
    <Link to={`/ep-v2/${s.epId}`} className="suspension-remobilisation__lien-ep" target="_blank" rel="noopener noreferrer">
      {date}
    </Link>
  ) : (
    date
  );
};

// Corps d'une entrée : ce qui explique la demande à cette étape.
export const TimelineEventSuspensionRemobilisationBody = ({ event }) => {
  const { etape, demande: d } = event;
  const lignes = [];

  if (etape === "demande") {
    if (demandeur(d)) lignes.push({ label: "Demandée par", value: demandeur(d) });
    if (d.targetedSanction) {
      lignes.push({
        label: "Sanction visée",
        value: `${d.targetedSanction.label}${d.targetedSanction.endOfPeriod ? `, jusqu'au ${tools.formatDate(d.targetedSanction.endOfPeriod)}` : ""}`,
      });
      lignes.push({ label: "Décidée à l'EP du", value: lienEP(d) });
    }
    if (d.reasonForLiftingLabel) lignes.push({ label: "Motif", value: d.reasonForLiftingLabel });
    if (d.partnerJustification) lignes.push({ label: "Justificatif France Travail", value: d.partnerJustification });
  }
  if (etape === "retrait" && !d.epRegistrationWithdrawal?.done) {
    lignes.push({ label: "Raison", value: d.epRegistrationWithdrawal?.reasonNotDone || "—" });
  }
  if (etape === "decision" && d.directorDecision) {
    if (d.targetedSanction) lignes.push({ label: "Sanction levée", value: d.targetedSanction.label });
    if (d.targetedSanction?.epDate) lignes.push({ label: "Décidée à l'EP du", value: lienEP(d) });
    lignes.push({ label: "Par", value: nomComplet(d.directorDecision.by) || d.directorDecision.by?.id });
    if (d.directorDecision.refusalReasonLabel) lignes.push({ label: "Motif du refus", value: d.directorDecision.refusalReasonLabel });
    // Seule une remobilisation validée donne lieu à un courrier à l'allocataire.
    if (d.status === "VALIDEE" && d.outcome === "REMOBILISATION") {
      lignes.push({ label: "Courrier", value: <VoirCourrierRemobilisation demandeId={d.id} /> });
    }
  }

  if (lignes.length === 0) return null;
  return (
    <div className="tl__body">
      <dl className="tl-ft__rows">
        {lignes.map((l, k) => (
          <div key={`${event.id}-${k}`} className="tl-ft__row">
            <dt>{l.label}</dt>
            <dd>{l.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export const buildSuspensionRemobilisationItem = (event) => {
  const { etape, demande } = event;
  return {
    id: event.id,
    date: event.date,
    icon: ICONES[etape] ?? "unlock-alt",
    color: colors[couleur(event)],
    badges: (
      <>
        <Pill tone="dark">Sanction</Pill>
        <Pill tone="muted">{demande.outcomeLabel}</Pill>
        {etape !== "retrait" && <Pill tone={STATUT_TONES[demande.status] ?? "neutral"}>{demande.statusLabel}</Pill>}
      </>
    ),
    title: (TITRES[etape] ?? TITRES.demande)(demande),
    body: <TimelineEventSuspensionRemobilisationBody event={event} />,
  };
};

export default TimelineEventSuspensionRemobilisationBody;
